import { useCallback } from 'react';
import {
  useCheckNewNotificationsQuery,
  useMarkNotificationViewedMutation,
  useRemoveNotificationMutation,
  useNotificationSummaryQuery,
} from './api';

/**
 * Hook to fetch notifications with feature registration
 *
 * @param options - Query options including polling interval, skip, etc.
 */
export function useGetNotifications(options?: {
  pollingInterval?: number;
  refetchOnFocus?: boolean;
  skip?: boolean;
}) {
  return useCheckNewNotificationsQuery(undefined, {
    pollingInterval: options?.pollingInterval,
    refetchOnFocus: options?.refetchOnFocus,
    skip: options?.skip,
  });
}

/**
 * Hook to mark a notification as viewed
 */
export function useMarkNotificationViewed() {
  const [markNotificationViewed, result] = useMarkNotificationViewedMutation();

  const mutate = useCallback(
    (notificationId: string, options?: { onSuccess?: () => void; onError?: () => void }) => {
      const promise = markNotificationViewed(notificationId);

      // Handle options manually for consistent interface with other hooks
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(() => options.onError?.());
      }

      return promise;
    },
    [markNotificationViewed],
  );

  return {
    mutate,
    ...result,
  };
}

/**
 * Hook to remove a notification
 */
export function useRemoveNotification() {
  const [removeNotification, result] = useRemoveNotificationMutation();

  const mutate = useCallback(
    (notificationId: string, options?: { onSuccess?: () => void; onError?: () => void }) => {
      const promise = removeNotification(notificationId);

      // Handle options manually for consistent interface with other hooks
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(() => options.onError?.());
      }

      return promise;
    },
    [removeNotification],
  );

  return {
    mutate,
    ...result,
  };
}

/**
 * Hook for notifications with unified interface
 *
 * Combines all notification functionality in a single hook for convenience
 * while still ensuring feature registration.
 */
export function useNotifications(options?: { pollingInterval?: number; refetchOnFocus?: boolean }) {
  // Use RTK Query hooks
  const { data: notifications = [] } = useCheckNewNotificationsQuery(undefined, {
    pollingInterval: options?.pollingInterval || 10 * 60 * 1000, // Default: 10 minutes
    refetchOnFocus: options?.refetchOnFocus || false,
  });

  const { mutate: markOpened } = useMarkNotificationViewed();
  const { mutate: remove } = useRemoveNotification();

  // Calculate unread count
  const unreadCount = notifications.filter(n => n.isOpened === 0).length;

  return {
    notifications,
    unreadCount,
    markOpened,
    remove,
  };
}

const NOTIFICATION_SUMMARY_POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

/**
 * Hook to poll the notification summary endpoint for badge counts.
 * Designed to be called from app-level components (Nav) so polling runs globally.
 * RTK Query deduplicates subscriptions, so multiple consumers share one poll.
 */
export function useNotificationSummaryPolling(options?: { skip?: boolean }) {
  const { data, isLoading, isFetching } = useNotificationSummaryQuery(undefined, {
    pollingInterval: NOTIFICATION_SUMMARY_POLL_INTERVAL_MS,
    refetchOnFocus: true,
    skip: options?.skip,
  });

  const cosignQueueCount = data?.items?.find(i => i.key === 'cosign_queue')?.count ?? 0;
  const editsRequestedCount = data?.items?.find(i => i.key === 'edits_requested')?.count ?? 0;
  const totalCount = data?.total_count ?? 0;
  // data.total_count sums across all categories (notes, prescriptions, …). Badge
  // surfaces attached to the Notes nav want only the note-queue items.
  const notesTotalCount =
    data?.items
      ?.filter(i => (i as { category?: string }).category === 'note_queues')
      .reduce((sum, i) => sum + (i.count ?? 0), 0) ?? 0;

  return {
    cosignQueueCount,
    editsRequestedCount,
    notesTotalCount,
    totalCount,
    isLoading,
    isFetching,
  };
}
